/**
 * Unified keyboard + gamepad input (PRD §5). Analog values pass through untouched
 * from a stick; digital keys are ramped so keyboard play still feels analog-ish.
 *
 * Menus read the same state — `menuUp`/`menuDown`/`confirm`/`back` are the
 * edge-triggered view of the movement axis and the fire key, so the shop and
 * the pause screen work on a keyboard, a pad and a thumb without a second
 * input path.
 */

export interface InputState {
  /** -1..1, right positive */
  moveX: number;
  /** -1..1, up positive */
  moveY: number;
  /**
   * Gun elevation demand, -1..1, up positive. Independent of `moveY`: this is
   * where the turret is pointed, not where the boat is going.
   */
  gunY: number;
  /**
   * Whether `gunY` is a real demand this frame rather than an absent one.
   *
   * Zero is a legitimate value — "level" — and is indistinguishable from "no
   * device is asking", so the two cannot be carried on one float. Without this
   * the gun would snap to level the instant a thumb left the fire stick,
   * instead of handing the turret back to the aim it follows by default.
   */
  gunAim: boolean;
  fire: boolean;
  firePressed: boolean;
  torpedo: boolean;
  torpedoPressed: boolean;
  charge: boolean;
  chargePressed: boolean;
  lock: boolean;
  sonar: boolean;
  sonarPressed: boolean;
  /** Nitro. Held, not toggled. */
  boost: boolean;
  light: boolean;
  lightPressed: boolean;
  /** Grapple. Tap to fire or cut; hold to winch a load in. */
  grab: boolean;
  grabPressed: boolean;
  /** Collapse or expand the ore hold readout. Edge-triggered. */
  holdPressed: boolean;
  pausePressed: boolean;
  dockPressed: boolean;
  mapPressed: boolean;
  anyPressed: boolean;
  usingGamepad: boolean;

  /**
   * View controls. Presentation only — none of this reaches the simulation.
   *
   * All three are per-frame deltas rather than absolute state, because the
   * events that feed them arrive at whatever rate the pointer hardware feels
   * like and have no notion of a frame. The camera integrates them.
   */
  /** Zoom steps this frame. Positive pulls the camera back. */
  zoomDelta: number;
  /** Pan this frame, in fractions of the visible half-extent. */
  viewPanX: number;
  viewPanY: number;
  /**
   * Camera tilt this frame, in radians. Positive raises the eye and looks down.
   *
   * A DELTA and not an angle, like zoom and pan beside it, because the only
   * device that produces it is a held stick and a held stick is a rate.
   */
  viewTilt: number;
  /** Snap the view back to the tuned default. */
  viewReset: boolean;

  // Menu-only, all edge-triggered.
  menuUp: boolean;
  menuDown: boolean;
  menuLeft: boolean;
  menuRight: boolean;
  confirm: boolean;
  back: boolean;
}

const digital = {
  up: false, down: false, left: false, right: false,
  gunUp: false, gunDown: false,
  fire: false, torpedo: false, charge: false, lock: false,
  sonar: false, light: false, pause: false, boost: false,
  dock: false, map: false, confirm: false, grab: false,
  hold: false,
};

const KEY_MAP: Record<string, keyof typeof digital> = {
  // THE ARROWS ARE THE MOVEMENT KEYS AND W/S IS THE GUN.
  //
  // W and S were vertical movement for the whole life of this game, so this
  // costs every existing player their dive-and-climb muscle memory and is
  // worth saying out loud. What it buys is a scheme where the left hand aims
  // and the right hand flies, on a keyboard that has no second stick — and
  // the alternative was a pair of letters nobody would ever find.
  //
  // A and D stay on movement. The left hand still steers with them, and taking
  // them away would have left that hand holding two keys and nothing else.
  ArrowUp: 'up',
  ArrowDown: 'down',
  KeyA: 'left', ArrowLeft: 'left',
  KeyD: 'right', ArrowRight: 'right',
  Space: 'fire',
  // Q and E sit under the fingers already on WASD.
  KeyQ: 'torpedo', KeyF: 'torpedo',
  KeyC: 'charge', KeyX: 'charge',
  KeyE: 'sonar',
  // Shift is the nitro; holding a lock moved to a key that is never held by
  // accident while sprinting.
  ShiftLeft: 'boost', ShiftRight: 'boost',
  // Ctrl only. K used to be a second way to hold the torpedo lock, undocumented
  // — it is not in the on-screen keymap and never has been — and the gun needed
  // the letter more than a redundant alias did.
  ControlLeft: 'lock', ControlRight: 'lock',
  // GUN ELEVATION. W over S, where the thumb that fires already lives — and
  // I over K as the alias, the same two rows one hand to the right, for anyone
  // who would rather fly with the left hand and aim with the right.
  KeyW: 'gunUp', KeyI: 'gunUp',
  KeyS: 'gunDown', KeyK: 'gunDown',
  KeyL: 'light',
  // G for grapple, R for the hand already on WASD.
  KeyG: 'grab', KeyR: 'grab',
  KeyU: 'dock', Enter: 'confirm',
  // H for the hold, which is the readout it collapses.
  KeyH: 'hold',
  Tab: 'map',
  Escape: 'pause', KeyP: 'pause',
};

/**
 * View keys, held rather than tapped, so they are ramped by time and not by
 * the OS key-repeat rate. Kept out of `digital` because nothing in the game
 * reads them — they never reach `InputState` as buttons.
 */
const view = { zoomIn: false, zoomOut: false, reset: false };

const VIEW_MAP: Record<string, keyof typeof view> = {
  Minus: 'zoomOut', NumpadSubtract: 'zoomOut',
  Equal: 'zoomIn', NumpadAdd: 'zoomIn',
  Digit0: 'reset', Numpad0: 'reset',
};

import type { TouchInput } from './touch';

export class Input {
  touch: TouchInput | null = null;

  readonly state: InputState = {
    moveX: 0, moveY: 0, gunY: 0, gunAim: false,
    fire: false, firePressed: false,
    torpedo: false, torpedoPressed: false,
    charge: false, chargePressed: false,
    lock: false, sonar: false, sonarPressed: false,
    boost: false, light: false, lightPressed: false,
    grab: false, grabPressed: false,
    holdPressed: false,
    pausePressed: false, dockPressed: false, mapPressed: false,
    anyPressed: false, usingGamepad: false,
    zoomDelta: 0, viewPanX: 0, viewPanY: 0, viewTilt: 0, viewReset: false,
    menuUp: false, menuDown: false, menuLeft: false, menuRight: false,
    confirm: false, back: false,
  };

  /**
   * Wheel events arrive whenever the hardware feels like it — a trackpad fling
   * fires dozens between two frames — so they accumulate here and are drained
   * once per `update`. Reading them directly off the event would make the zoom
   * rate depend on the frame rate.
   */
  private accZoom = 0;
  private accPanX = 0;
  private accPanY = 0;

  private ramp = { x: 0, y: 0, g: 0 };
  private prev = {
    fire: false, torpedo: false, charge: false, sonar: false, light: false,
    pause: false, dock: false, map: false, confirm: false, grab: false,
    hold: false,
    navUp: false, navDown: false, navLeft: false, navRight: false,
  };
  private padIndex: number | null = null;

  constructor(target: EventTarget = window) {
    target.addEventListener('keydown', (e) => this.key(e as KeyboardEvent, true));
    target.addEventListener('keyup', (e) => this.key(e as KeyboardEvent, false));
    window.addEventListener('blur', () => {
      for (const k of Object.keys(digital) as (keyof typeof digital)[]) digital[k] = false;
      for (const k of Object.keys(view) as (keyof typeof view)[]) view[k] = false;
    });
    window.addEventListener('gamepadconnected', (e) => {
      this.padIndex = (e as GamepadEvent).gamepad.index;
    });
    window.addEventListener('gamepaddisconnected', () => {
      this.padIndex = null;
    });
    // `passive: false` is required: without it the browser refuses the
    // preventDefault below, and a pinch zooms the whole page instead of the
    // camera while a two-finger scroll bounces the document.
    window.addEventListener('wheel', (e) => this.wheel(e), { passive: false });
  }

  /**
   * Trackpad and wheel, mapped the way every native application on this
   * platform maps them: PINCH ZOOMS, TWO FINGERS PAN.
   *
   * The temptation is to make a plain wheel zoom, since that is what a mouse
   * wheel does in most games — but a trackpad's two-finger scroll and a mouse
   * wheel are THE SAME EVENT, separable only by guessing from the size and
   * granularity of the deltas. That guess is wrong often enough to make the
   * control feel broken, and it is wrong in the worst way: silently, and
   * differently on different hardware. So the gesture decides, not the
   * hardware, and a mouse keeps a zoom via ALT — plus `-`/`=` for both.
   */
  private wheel(e: WheelEvent) {
    // NOT OVER A PANEL. This listener is on `window` and calls preventDefault
    // unconditionally, which is right for the game and wrong for anything
    // drawn in the DOM on top of it: the tuner is a scrolling list, and its
    // wheel events were being cancelled before the browser could scroll it
    // while ALSO panning the camera underneath. One gesture, two wrong
    // outcomes.
    //
    // Marked on the overlay rather than named here, so the next panel gets it
    // without this file having to learn about it.
    if (e.target instanceof Element && e.target.closest('[data-overlay]')) return;
    e.preventDefault();
    // deltaMode 1 is lines and 2 is pages; normalise both to pixels.
    const k = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? 400 : 1;
    const dx = e.deltaX * k, dy = e.deltaY * k;
    // A pinch on a trackpad arrives as a wheel event with `ctrlKey` set,
    // whether or not ctrl is physically down. This is the only signal in the
    // platform that says "pinch", and it is reliable.
    if (e.ctrlKey || e.altKey) this.accZoom += dy * 0.006;
    else { this.accPanX += dx; this.accPanY += dy; }
  }

  private key(e: KeyboardEvent, down: boolean) {
    const v = VIEW_MAP[e.code];
    if (v) { view[v] = down; return; }
    const slot = KEY_MAP[e.code];
    if (!slot) return;
    if (e.repeat) return;
    digital[slot] = down;
    // PageUp/PageDown scroll the document, and the document behind the canvas
    // is exactly as tall as the window — so the scroll goes nowhere visible and
    // the browser eats the keypress on the way. Same reason Space is here.
    if (e.code === 'Space' || e.code === 'Tab' || e.code.startsWith('Arrow')) e.preventDefault();
  }

  private pad(): Gamepad | null {
    const pads = navigator.getGamepads?.() ?? [];
    if (this.padIndex !== null && pads[this.padIndex]) return pads[this.padIndex];
    for (const p of pads) if (p && p.connected) { this.padIndex = p.index; return p; }
    return null;
  }

  update(dt: number) {
    const s = this.state;
    const p = this.pad();

    let ax = 0, ay = 0;
    let gunY = 0, gunAim = false;
    let viewZoom = 0, viewTilt = 0, viewReset = view.reset;
    let padActive = false;
    let fire = digital.fire, torp = digital.torpedo, lock = digital.lock;
    let sonar = digital.sonar, light = digital.light, pause = digital.pause;
    let boost = digital.boost, charge = digital.charge;
    let dock = digital.dock, map = digital.map, grab = digital.grab;
    let hold = digital.hold;
    let confirm = digital.confirm || digital.fire;

    if (p) {
      const dead = 0.18;
      const shape = (v: number) => {
        const a = Math.abs(v);
        if (a < dead) return 0;
        const t = (a - dead) / (1 - dead);
        return Math.sign(v) * t * t * (3 - 2 * t) * 1.05;
      };
      ax = shape(p.axes[0] ?? 0);
      ay = -shape(p.axes[1] ?? 0);
      // RIGHT STICK Y AIMS THE GUN. Axes 2 and 3 were the only unclaimed
      // inputs on a standard pad, and elevation is exactly what a right stick
      // is for — this is the one scheme of the three where aiming and firing
      // are already on different thumbs and neither has to give anything up.
      gunY = -shape(p.axes[3] ?? 0);
      if (gunY !== 0) gunAim = true;
      // D-pad fallback (buttons 12-15 on the standard mapping)
      if (p.buttons[14]?.pressed) ax = -1;
      if (p.buttons[15]?.pressed) ax = 1;
      if (p.buttons[12]?.pressed) ay = 1;
      if (p.buttons[13]?.pressed) ay = -1;
      const rt = p.buttons[7]?.value ?? 0;
      const lt = p.buttons[6]?.value ?? 0;
      fire ||= rt > 0.3 || !!p.buttons[0]?.pressed;
      torp ||= lt > 0.3 || !!p.buttons[2]?.pressed;
      lock ||= !!p.buttons[5]?.pressed;   // RB
      sonar ||= !!p.buttons[4]?.pressed;  // LB
      light ||= !!p.buttons[3]?.pressed;  // Y
      boost ||= !!p.buttons[1]?.pressed; // B
      charge ||= !!p.buttons[11]?.pressed; // R3
      // L3. It used to be a second way to light the nitro, which B already
      // does — a redundant alias is the cheapest button on a full pad.
      grab ||= !!p.buttons[10]?.pressed;
      dock ||= !!p.buttons[8]?.pressed;    // View / Select
      pause ||= !!p.buttons[9]?.pressed;
      confirm ||= !!p.buttons[0]?.pressed;
      padActive = Math.abs(ax) > 0.01 || Math.abs(ay) > 0.01 || fire || torp || sonar || boost;
    }

    const t = this.touch;
    // Any real key or stick deflection means the player is not on a phone.
    const keyActive =
      digital.up || digital.down || digital.left || digital.right ||
      digital.gunUp || digital.gunDown ||
      digital.fire || digital.torpedo || digital.sonar || digital.light || digital.boost;
    if (t && (keyActive || (p && padActive))) t.noteOtherInput();

    if (t && t.enabled) {
      t.update(dt);
      if (Math.abs(t.moveX) > Math.abs(ax) || Math.abs(t.moveY) > Math.abs(ay)) {
        ax = t.moveX;
        ay = t.moveY;
      }
      fire ||= t.pressed('fire');
      // The fire button IS the gun stick on a phone — see `stick` in touch.ts.
      // A thumb resting on it dead centre still counts as a demand: holding
      // fire without sliding means "straight ahead", which is a choice, and
      // letting it fall through to the default aim would make the gun drift
      // while the player was deliberately holding it level.
      if (t.pressed('fire')) { gunY = t.gunY; gunAim = true; }
      torp ||= t.pressed('torpedo');
      sonar ||= t.pressed('sonar');
      light ||= t.pressed('light');
      boost ||= t.pressed('nitro');
      charge ||= t.pressed('charge');
      dock ||= t.pressed('dock');
      // The phone's way back to the menu. Edge-triggered like the key, so
      // holding it does not reopen the screen every frame.
      pause ||= t.pressed('menu');
      // The phone's collapse for the ore readout. Edge-triggered like the
      // key, or holding it would flap the panel open and shut every frame.
      hold ||= t.pressed('hold');
      grab ||= t.pressed('grab');
      confirm ||= t.pressed('fire');
      // Holding fire also holds the lock, so a phone needs no extra button.
      lock ||= t.pressed('fire');
      // THE VIEW STICK. Right zooms in, up tilts the eye over — pushing toward
      // the subject brings you closer, pushing up raises the camera, which is
      // the only pair of signs that does not need to be memorised.
      //
      // Rates, not positions: the stick springs back to centre, so an absolute
      // reading would snap the view home the instant the thumb lifted. Zoom
      // matches the held-key rate exactly, so a phone and a keyboard travel the
      // range in the same time.
      viewZoom -= t.viewX * dt * 1.6;
      viewTilt += t.viewY * dt * 0.35;
      viewReset = viewReset || t.consumeViewTap();
      padActive = padActive || fire || torp || sonar ||
        Math.abs(t.moveX) > 0.01 || Math.abs(t.moveY) > 0.01;
    }

    // Keyboard ramp — reach full deflection in ~120 ms, release in ~70 ms.
    const tx = (digital.right ? 1 : 0) - (digital.left ? 1 : 0);
    const ty = (digital.up ? 1 : 0) - (digital.down ? 1 : 0);
    const rate = (v: number, cur: number) => (v === 0 ? 26 : Math.sign(v) === Math.sign(cur) || cur === 0 ? 14 : 26);
    this.ramp.x += (tx - this.ramp.x) * Math.min(1, rate(tx, this.ramp.x) * dt);
    this.ramp.y += (ty - this.ramp.y) * Math.min(1, rate(ty, this.ramp.y) * dt);
    // The gun rides the same ramp shape as movement, so a tapped key gives a
    // nudge and a held one runs to the stop — a turret that jumped to full
    // elevation on the down edge would be unaimable at anything in between.
    //
    // `gunAim` follows the RAMP and not the key, so releasing eases the barrel
    // back to the default aim instead of dropping it there in one frame.
    const tg = (digital.gunUp ? 1 : 0) - (digital.gunDown ? 1 : 0);
    this.ramp.g += (tg - this.ramp.g) * Math.min(1, rate(tg, this.ramp.g) * dt);
    // Largest wins, the same rule the movement axis uses one line down, so a
    // pad and a keyboard can be on the desk at once without either latching.
    if (Math.abs(this.ramp.g) > Math.abs(gunY)) gunY = this.ramp.g;
    if (Math.abs(this.ramp.g) > 0.01) gunAim = true;

    s.moveX = Math.abs(ax) > Math.abs(this.ramp.x) ? ax : this.ramp.x;
    s.moveY = Math.abs(ay) > Math.abs(this.ramp.y) ? ay : this.ramp.y;
    s.gunY = gunY;
    s.gunAim = gunAim;
    s.usingGamepad = padActive || (s.usingGamepad && !tx && !ty);

    s.fire = fire;
    s.lock = lock;
    s.boost = boost;
    s.torpedo = torp;
    s.charge = charge;
    s.sonar = sonar;
    s.light = light;
    s.grab = grab;
    s.grabPressed = grab && !this.prev.grab;
    s.holdPressed = hold && !this.prev.hold;
    s.firePressed = fire && !this.prev.fire;
    s.torpedoPressed = torp && !this.prev.torpedo;
    s.chargePressed = charge && !this.prev.charge;
    s.sonarPressed = sonar && !this.prev.sonar;
    s.lightPressed = light && !this.prev.light;
    s.pausePressed = pause && !this.prev.pause;
    s.dockPressed = dock && !this.prev.dock;
    s.mapPressed = map && !this.prev.map;
    s.anyPressed = s.torpedoPressed || s.sonarPressed || s.firePressed;

    // View controls. Drained here so the accumulated wheel deltas are consumed
    // exactly once per frame whatever rate they arrived at, and the held keys
    // are ramped by time so the zoom rate does not follow the frame rate.
    const keyZoom = (view.zoomOut ? 1 : 0) - (view.zoomIn ? 1 : 0);
    s.zoomDelta = this.accZoom + keyZoom * dt * 1.6 + viewZoom;
    s.viewPanX = this.accPanX;
    s.viewPanY = this.accPanY;
    s.viewTilt = viewTilt;
    s.viewReset = viewReset;
    this.accZoom = 0;
    this.accPanX = 0;
    this.accPanY = 0;

    // Menu navigation off the same axis, with a dead zone wide enough that a
    // resting stick never scrolls a list.
    // W and S no longer move the boat, but a menu is not a boat: taking the two
    // keys every player reaches for first OUT of list navigation would be a
    // gratuitous second cost on top of the flying one. The gun axis drives the
    // list as well, so arrows, WASD and a stick all walk it.
    const navUp = s.moveY > 0.55 || this.ramp.g > 0.55;
    const navDown = s.moveY < -0.55 || this.ramp.g < -0.55;
    const navLeft = s.moveX < -0.55;
    const navRight = s.moveX > 0.55;
    s.menuUp = navUp && !this.prev.navUp;
    s.menuDown = navDown && !this.prev.navDown;
    s.menuLeft = navLeft && !this.prev.navLeft;
    s.menuRight = navRight && !this.prev.navRight;
    s.confirm = confirm && !this.prev.confirm;
    s.back = s.pausePressed;

    this.prev.fire = fire;
    this.prev.torpedo = torp;
    this.prev.charge = charge;
    this.prev.sonar = sonar;
    this.prev.light = light;
    this.prev.pause = pause;
    this.prev.dock = dock;
    this.prev.map = map;
    this.prev.grab = grab;
    this.prev.hold = hold;
    this.prev.confirm = confirm;
    this.prev.navUp = navUp;
    this.prev.navDown = navDown;
    this.prev.navLeft = navLeft;
    this.prev.navRight = navRight;
  }
}
